import Tabs, { TabList, TabPanel } from '@atlaskit/tabs';
import { css } from '@compiled/react';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { ReactElement } from 'react';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { MyLearningContentSection } from 'src/pages/MyLearningPage/children/MyLearningContentSection';
import { RecentlyViewedLessonSection } from 'src/pages/MyLearningPage/children/RecentlyViewedLessonSection';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { sendUIEvent } from 'src/utils/analytics';
import { useAtlassianUser } from 'src/hooks/useAtlassianUser';
import { MyLearningLoaderProps } from 'src/pages/MyLearningPage';
import { ArchivedContentSection } from 'src/pages/MyLearningPage/children/ArchivedContentSection';
import { ALM_INTEGRATION_FF } from 'src/constants';
import { CustomTab } from 'src/pages/MyLearningPage/children/CustomTab';

export const ON_DEMAND_TAB_PATHS = ['in-progress', 'saved', 'complete'];

interface MyLearningOnDemandTabsProps {
  activeTabIndex: number;
  category?: string;
}

export const MyLearningOnDemandTabs = ({ category, activeTabIndex }: MyLearningOnDemandTabsProps): ReactElement => {
  const {
    recentlyViewedLessonsResources,
    myLearningResourceData,
    archivedContentResources,
    areUnseenNotificationsAvailable,
  } = useLoaderData() as MyLearningLoaderProps;
  const { user } = useAtlassianUser();

  const { status } = useParams();
  const navigate = useNavigate();

  const handleTabChange = (index: number): void => {
    if (status === ON_DEMAND_TAB_PATHS[index]) {
      return;
    }

    const isALMIntegrationEnabled = FeatureGates.checkGate(ALM_INTEGRATION_FF);

    if (isALMIntegrationEnabled && category !== undefined) {
      // category is passed for MyLearningPageV2
      navigate(`/my-learning/${category}/${ON_DEMAND_TAB_PATHS[index]}`);
    } else {
      navigate(`/my-learning/${ON_DEMAND_TAB_PATHS[index]}`);
    }

    user &&
      sendUIEvent(
        'myLearningScreen',
        'tab',
        'clicked',
        'myLearningTab',
        {
          learningTab: ON_DEMAND_TAB_PATHS[index],
        },
        user.account_id,
      );
  };

  return (
    <div css={tabsListContainerStyles} data-testid="my-learning-tabs">
      <Tabs onChange={handleTabChange} id="default" defaultSelected={activeTabIndex} selected={activeTabIndex}>
        <TabList>
          <CustomTab label="In progress" />
          <CustomTab label="Saved" />
          <CustomTab label="Complete" areUnseenNotificationsAvailable={areUnseenNotificationsAvailable} />
        </TabList>
        {ON_DEMAND_TAB_PATHS.map((path) => (
          <TabPanel key={path}>
            <div css={emptyTabContainerStyles} key={path}>
              <MyLearningContentSection
                status={path}
                myLearningResourceData={myLearningResourceData}
                handleTabChange={handleTabChange}
              />
              {path === MyLearningPageContentSections.IN_PROGRESS && recentlyViewedLessonsResources && (
                <RecentlyViewedLessonSection
                  resources={recentlyViewedLessonsResources}
                  handleTabChange={handleTabChange}
                />
              )}
              {path === MyLearningPageContentSections.COMPLETE && archivedContentResources && (
                <ArchivedContentSection resources={archivedContentResources} />
              )}
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};

const tabsListContainerStyles = css({
  marginTop: '28px',
});

const emptyTabContainerStyles = css({
  width: '100%',
  margin: '0 auto',
});
